import { useEffect, useMemo, useState, type CSSProperties } from 'react';
import { useNavigate, useOutletContext } from 'react-router-dom';
import { Button } from '@helios/ui/button';
import { Modal } from '@helios/ui/overlay';
import { useAuth, usePermissions } from '@helios/auth-firebase-web';
import {
  qAppConfig,
  qAvailableBatchesForDispense,
  qGlobalPharmacyDispenseHistory,
  qPatientWorkspaceSettings,
  qPharmacyQueue,
  qTodaysPrescriptions,
  mapPatientWorkspaceSettingsToUI,
  type AppConfigRow,
  type PatientPharmacyDispenseHistoryRow,
  type PatientWorkspaceSettingsRow,
  type PharmacyBatchOptionRow,
  type PharmacyQueueRow,
  type TodaysPrescriptionRow,
} from '@helios/data-access';
import { usePowerSyncDb, usePowerSyncQuery } from '../../adapters/powersync';
import { PERM } from '../../auth/permissionMap';
import { useInvoiceModuleEnabled } from '../../hooks/useClinicMainModules';
import { useBrandTokens, type ShellOutletContext } from '../../shell/shared';
import { PrescriptionPrintMount, type PrescriptionPrintRequest } from './PrescriptionPrintMount';
import { PharmacyLabelPrintMount, type PharmacyLabelData } from './PharmacyLabelPrint';
import {
  PharmacyPrescriptionDrawer,
  type PharmacyPrescriptionContext,
} from './PharmacyPrescriptionDrawer';
import { PharmacyStockCardDrawer, type PharmacyStockCardContext } from './PharmacyStockCardDrawer';
import { TreatmentInvoiceDrawer } from '../patient/encounters/TreatmentInvoiceDrawer';
import { NewPharmacySaleDrawer } from './NewPharmacySaleDrawer';
import {
  TodaysPrescriptionInvoiceDrawer,
  type TodaysPrescriptionInvoiceContext,
} from './TodaysPrescriptionInvoiceDrawer';
import { PatientDetailsDrawer } from '../../shell/PatientDetailsDrawer';
import {
  InventoryListToolbar,
  InventoryPageShell,
  InventoryRow,
  InventoryStatusBadge,
  InventoryTable,
} from './_shared';

// PRD.289 — Billing drawer scope for the active Pharmacy row. Reuses the
// existing Billing UI (invoices + payments) without navigating to the
// Patient Workspace.
interface PharmacyBillingContext {
  patientId: string;
  encounterId: string | null;
  date: string | null;
  /** Invoice to preselect when the Billing drawer opens. */
  invoiceId: string | null;
}

// ─── Local state ───

interface QueueLineState {
  /** Selected batch for this line (mandatory before Confirm). */
  batchId: string | null;
  batchNumber: string | null;
  batchExpiry: string | null;
  batchAvailable: number | null;
  /** Outcome message after confirm (success/error). */
  message: { kind: 'ok' | 'error'; text: string } | null;
  /** Disable double-click while a confirm is in flight. */
  saving: boolean;
}

const COLUMNS = [
  { label: 'Date', width: '110px' },
  { label: 'Patient', width: '1fr' },
  // PRD.289 — Prescription column owns prescription metadata + Print Rx.
  { label: 'Prescription', width: '1fr' },
  // PRD.289 — Invoice column owns "open Billing focused on this invoice".
  { label: 'Invoice', width: '150px' },
  { label: 'Medicine', width: '1.3fr' },
  { label: 'Batch', width: '180px' },
  { label: 'Qty', width: '110px', align: 'right' as const },
  { label: 'Status', width: '120px' },
  { label: 'Actions', width: '150px', align: 'right' as const },
];

// PRD.289 — shared style for the compact secondary row actions
// (Prescription / Billing / Print Rx) beneath the primary Confirm Dispense.
const secondaryActionStyle: CSSProperties = {
  height: 24,
  padding: '0 8px',
  borderRadius: 'var(--radius-sm)',
  border: '1px solid var(--border)',
  background: 'var(--bg-surface)',
  color: 'var(--text-muted)',
  fontSize: 10,
  fontWeight: 500,
  cursor: 'pointer',
};

// PRD.289 — every queue-row cell is top-anchored (grid rows center by default),
// and its primary value sits in a fixed-height band so all first-line values
// line up across columns; any secondary content (SKU, Print Rx, Open Billing,
// doctor) flows beneath.
const PRIMARY_LINE_HEIGHT = 34;
const cellTopStyle: CSSProperties = { alignSelf: 'start', minWidth: 0 };
const primaryLineStyle: CSSProperties = {
  display: 'flex',
  alignItems: 'center',
  minHeight: PRIMARY_LINE_HEIGHT,
};
// A primary value rendered as a link-style button (patient / invoice / medicine
// / prescription). Fills the band so the whole first line is the click target.
const primaryLinkStyle: CSSProperties = {
  ...primaryLineStyle,
  width: '100%',
  background: 'none',
  border: 'none',
  padding: 0,
  textAlign: 'left',
  fontFamily: 'inherit',
  cursor: 'pointer',
  fontSize: 'var(--font-size-sm)',
  fontWeight: 'var(--font-weight-medium)',
  color: 'var(--color-primary)',
};

// ─── PRD.289 §2 — queue sorting ───

type QueueSortKey = 'date_desc' | 'date_asc' | 'patient' | 'medicine';

const SORT_OPTIONS: { value: QueueSortKey; label: string }[] = [
  { value: 'date_desc', label: 'Invoice date (newest)' },
  { value: 'date_asc', label: 'Invoice date (oldest)' },
  { value: 'patient', label: 'Patient name (A–Z)' },
  { value: 'medicine', label: 'Medicine (A–Z)' },
];

const toolbarSelectStyle: CSSProperties = {
  height: 'var(--input-height)',
  padding: '0 var(--input-padding-x)',
  borderRadius: 'var(--input-radius)',
  border: '1px solid var(--border-input)',
  background: 'var(--input-bg)',
  fontSize: 'var(--font-size-sm)',
  color: 'var(--input-text)',
  outline: 'none',
  cursor: 'pointer',
};

// PRD.289 — compact date for the Prescription column (accepts a full ISO
// timestamp or YYYY-MM-DD). Returns null when unparseable/empty.
function formatShortDate(iso: string | null | undefined): string | null {
  if (!iso) return null;
  const d = new Date(iso.length > 10 ? iso : `${iso}T00:00:00`);
  if (Number.isNaN(d.getTime())) return iso.slice(0, 10);
  return d.toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
}

function queueMedicineName(row: PharmacyQueueRow): string {
  return (row.item_name ?? row.description_snapshot ?? '').toLowerCase();
}

/** Stable sort of the queue rows by the chosen key (non-mutating). */
function sortQueueRows(rows: PharmacyQueueRow[], key: QueueSortKey): PharmacyQueueRow[] {
  // 'date_desc' is the query's native order — avoid a redundant re-sort.
  if (key === 'date_desc') return rows;
  const next = [...rows];
  next.sort((a, b) => {
    switch (key) {
      case 'date_asc':
        return (a.invoice_date ?? '').localeCompare(b.invoice_date ?? '');
      case 'patient':
        return (a.patient_name ?? '').localeCompare(b.patient_name ?? '');
      case 'medicine':
        return queueMedicineName(a).localeCompare(queueMedicineName(b));
      default:
        return 0;
    }
  });
  return next;
}

export function PharmacyPage() {
  const { collapsed = false, onToggleSidebar } = useOutletContext<ShellOutletContext>() ?? {
    collapsed: false,
  };
  const brandTokens = useBrandTokens();
  const navigate = useNavigate();
  const { db } = usePowerSyncDb();
  const { claims, user } = useAuth();
  const { canAccess } = usePermissions();
  const orgId = claims?.activeOrg ?? '';
  const userId = user?.uid ?? '';
  const userName = user?.displayName ?? user?.email ?? '';

  // PRD.289 — the Billing action reuses the existing Billing surface. It is
  // gated by the same two rules as the Queue "Pay" action: the Billing
  // permission and the clinic Invoice module toggle.
  const canBilling = canAccess(PERM.billing);
  const invoiceModuleEnabled = useInvoiceModuleEnabled();
  const billingEnabled = canBilling && invoiceModuleEnabled;

  const { data: workspaceRows = [] } = usePowerSyncQuery<PatientWorkspaceSettingsRow>(
    qPatientWorkspaceSettings(),
  );
  const billingDrawerWidth = useMemo(
    () =>
      workspaceRows.length > 0
        ? mapPatientWorkspaceSettingsToUI(workspaceRows[0]).treatmentRecordDrawerWidth
        : 'standard',
    [workspaceRows],
  );

  const { data: queueRows = [] } = usePowerSyncQuery<PharmacyQueueRow>(qPharmacyQueue());
  // PRD.204 — global dispense history feed (read-only).
  const { data: historyRows = [] } = usePowerSyncQuery<PatientPharmacyDispenseHistoryRow>(
    qGlobalPharmacyDispenseHistory(),
  );
  const { data: appConfigRows = [] } = usePowerSyncQuery<AppConfigRow>(qAppConfig());
  const clinicName = appConfigRows[0]?.org_name ?? null;

  // PRD.291 — Today's prescriptions (local "today" so day boundaries match the
  // rest of the app rather than SQLite's UTC date('now')).
  const todayIso = useMemo(() => {
    const d = new Date();
    const off = d.getTimezoneOffset();
    return new Date(d.getTime() - off * 60000).toISOString().slice(0, 10);
  }, []);
  const { data: todaysRxRows = [] } = usePowerSyncQuery<TodaysPrescriptionRow>(
    qTodaysPrescriptions(todayIso),
  );

  // PRD.204 — view-mode toggle: Ready queue (default) or read-only history.
  // PRD.291 — Today's Prescriptions tab added before Ready to Dispense.
  const [viewMode, setViewMode] = useState<'today' | 'queue' | 'history'>('queue');
  const [historyDateFilter, setHistoryDateFilter] = useState<'all' | 'today' | '7d' | '30d'>('all');

  const [search, setSearch] = useState('');
  // PRD.289 §2 — queue sort key. Defaults to the existing query order
  // (invoice date, newest first).
  const [sortKey, setSortKey] = useState<QueueSortKey>('date_desc');
  const [batchModalLineId, setBatchModalLineId] = useState<string | null>(null);
  // PRD.202 — Print Rx state. Triggers a hidden encounter prescription
  // print without leaving the global Pharmacy queue.
  const [rxPrintRequest, setRxPrintRequest] = useState<PrescriptionPrintRequest | null>(null);
  // PRD.202/204 — Print Label state for history rows.
  const [labelToPrint, setLabelToPrint] = useState<PharmacyLabelData | null>(null);
  // PRD.289 — read-only Prescription drawer + Billing drawer scopes. Opening
  // either keeps the pharmacist inside the Pharmacy module (no navigation to
  // the Patient Workspace).
  const [rxDrawer, setRxDrawer] = useState<PharmacyPrescriptionContext | null>(null);
  const [billingDrawer, setBillingDrawer] = useState<PharmacyBillingContext | null>(null);
  // PRD.289 — clicking a queue row's patient name opens the read-only patient
  // details drawer (same self-contained surface as the Queue "View Patient"
  // action — it never navigates to `/patient/:id`).
  const [viewPatientId, setViewPatientId] = useState<string | null>(null);
  // PRD.289 — clicking a queue row's medicine opens the read-only stock-card
  // movement ledger scoped to that inventory item.
  const [stockCardItem, setStockCardItem] = useState<PharmacyStockCardContext | null>(null);
  // PRD.290 — "New Pharmacy Sale" drawer (existing patients only).
  const [saleDrawerOpen, setSaleDrawerOpen] = useState(false);
  // PRD.291 — "Today's Prescriptions → Create Invoice" drawer scope.
  const [rxInvoiceDrawer, setRxInvoiceDrawer] = useState<TodaysPrescriptionInvoiceContext | null>(
    null,
  );

  // Per-invoice-item local state. Reset when the invoice item disappears
  // from the queue (e.g. after confirm — duplicate filter excludes it).
  const [lineState, setLineState] = useState<Record<string, QueueLineState>>({});
  useEffect(() => {
    const validIds = new Set(queueRows.map((r) => r.invoice_item_id));
    setLineState((prev) => {
      const next: Record<string, QueueLineState> = {};
      for (const [k, v] of Object.entries(prev)) if (validIds.has(k)) next[k] = v;
      return next;
    });
  }, [queueRows]);

  function patchLine(invoiceItemId: string, patch: Partial<QueueLineState>) {
    setLineState((prev) => {
      const base: QueueLineState = prev[invoiceItemId] ?? {
        batchId: null,
        batchNumber: null,
        batchExpiry: null,
        batchAvailable: null,
        message: null,
        saving: false,
      };
      return { ...prev, [invoiceItemId]: { ...base, ...patch } };
    });
  }

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const rows = q
      ? queueRows.filter(
          (row) =>
            (row.patient_name ?? '').toLowerCase().includes(q) ||
            (row.invoice_no ?? '').toLowerCase().includes(q) ||
            (row.item_name ?? '').toLowerCase().includes(q) ||
            (row.sku ?? '').toLowerCase().includes(q),
        )
      : queueRows;
    return sortQueueRows(rows, sortKey);
  }, [queueRows, search, sortKey]);

  // PRD.291 — Today's Prescriptions filtering (patient / doctor).
  const filteredTodays = useMemo(() => {
    const q = search.trim().toLowerCase();
    if (!q) return todaysRxRows;
    return todaysRxRows.filter(
      (r) =>
        (r.patient_name ?? '').toLowerCase().includes(q) ||
        (r.provider_name ?? '').toLowerCase().includes(q),
    );
  }, [todaysRxRows, search]);

  // PRD.204 — history filtering. Search matches patient/medicine/SKU/
  // dispense number/invoice/batch; date filter restricts by dispensed_at.
  const filteredHistory = useMemo(() => {
    const q = search.trim().toLowerCase();
    const now = Date.now();
    const dayMs = 86400000;
    const startOfToday = new Date();
    startOfToday.setHours(0, 0, 0, 0);
    const startOfTodayMs = startOfToday.getTime();
    return historyRows.filter((row) => {
      // Date filter
      if (historyDateFilter !== 'all') {
        const t = row.dispensed_at ? new Date(row.dispensed_at).getTime() : 0;
        if (Number.isNaN(t) || t === 0) return false;
        if (historyDateFilter === 'today' && t < startOfTodayMs) return false;
        if (historyDateFilter === '7d' && now - t > 7 * dayMs) return false;
        if (historyDateFilter === '30d' && now - t > 30 * dayMs) return false;
      }
      if (!q) return true;
      return (
        (row.patient_name ?? '').toLowerCase().includes(q) ||
        (row.item_name_snapshot ?? '').toLowerCase().includes(q) ||
        (row.sku_snapshot ?? '').toLowerCase().includes(q) ||
        (row.dispense_number ?? '').toLowerCase().includes(q) ||
        (row.invoice_no ?? '').toLowerCase().includes(q) ||
        (row.batch_number_snapshot ?? '').toLowerCase().includes(q)
      );
    });
  }, [historyRows, search, historyDateFilter]);

  async function confirmDispense(row: PharmacyQueueRow) {
    if (!db) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Local database not ready.' },
      });
      return;
    }
    if (!orgId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Missing organization context.' },
      });
      return;
    }
    const state = lineState[row.invoice_item_id];
    if (!state?.batchId) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'Select a batch before confirming.' },
      });
      return;
    }
    const qty = row.quantity_remaining ?? 0;
    if (qty <= 0) {
      patchLine(row.invoice_item_id, {
        message: { kind: 'error', text: 'No remaining quantity to dispense.' },
      });
      return;
    }
    if (state.batchAvailable != null && qty > state.batchAvailable) {
      patchLine(row.invoice_item_id, {
        message: {
          kind: 'error',
          text: `Selected batch only has ${state.batchAvailable} available.`,
        },
      });
      return;
    }

    patchLine(row.invoice_item_id, { saving: true, message: null });
    try {
      const dispenseId = crypto.randomUUID();
      const lineId = crypto.randomUUID();
      const now = new Date().toISOString();
      const dispenseNumber = `DISP-${now.slice(0, 10)}-${dispenseId.slice(0, 4)}`;
      const unit = row.inventory_unit ?? row.unit_snapshot ?? null;
      const itemName = row.item_name ?? row.description_snapshot ?? '';

      // 1) Header
      await db.execute(
        `INSERT INTO pharmacy_dispenses (
          id, org_id, dispense_number, patient_id, encounter_id, invoice_id,
          prescription_id, status, dispensed_at, dispensed_by, remarks,
          created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          dispenseId,
          orgId,
          dispenseNumber,
          row.patient_id,
          row.encounter_id,
          row.invoice_id,
          null, // prescription_id — v1 doesn't link prescriptions yet
          'confirmed',
          now,
          userName || userId || null,
          null,
          now,
          now,
        ],
      );

      // 2) Line
      await db.execute(
        `INSERT INTO pharmacy_dispense_lines (
          id, org_id, pharmacy_dispense_id, invoice_item_id, prescription_line_id,
          inventory_item_id, inventory_batch_id,
          item_name_snapshot, sku_snapshot, batch_number_snapshot, expiry_date_snapshot,
          unit_snapshot, instructions_snapshot,
          quantity_to_dispense, quantity_dispensed,
          status, remarks, created_at, updated_at
        ) VALUES (
          ?, ?, ?, ?, ?,
          ?, ?,
          ?, ?, ?, ?,
          ?, ?,
          ?, ?,
          ?, ?, ?, ?
        )`,
        [
          lineId,
          orgId,
          dispenseId,
          row.invoice_item_id,
          null, // prescription_line_id — not wired in v1
          row.inventory_item_id,
          state.batchId,
          itemName,
          row.sku,
          state.batchNumber,
          state.batchExpiry,
          unit,
          null, // instructions_snapshot — placeholder for future prescription join
          qty,
          qty,
          'confirmed',
          null,
          now,
          now,
        ],
      );

      // 3) Outbound stock movement — the actual stock-out event.
      await db.execute(
        `INSERT INTO stock_movements (
          id, org_id, inventory_item_id, inventory_batch_id,
          source_type, source_id, source_line_id,
          movement_type, quantity_in, quantity_out, unit,
          movement_date, remarks, created_by, created_at, updated_at
        ) VALUES (?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?, ?)`,
        [
          crypto.randomUUID(),
          orgId,
          row.inventory_item_id,
          state.batchId,
          'pharmacy_dispense',
          dispenseId,
          lineId,
          'pharmacy_dispense',
          0,
          qty,
          unit,
          now.slice(0, 10),
          null,
          userId || null,
          now,
          now,
        ],
      );

      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'ok', text: 'Dispensed.' },
      });
      // Row will disappear on next query refresh because it's now fully
      // dispensed (excluded by the queue WHERE clause).
    } catch (err) {
      const msg = err instanceof Error ? err.message : 'Confirm failed. Please try again.';
      patchLine(row.invoice_item_id, {
        saving: false,
        message: { kind: 'error', text: msg },
      });
    }
  }

  return (
    <InventoryPageShell
      title="Pharmacy"
      subtitle="Dispense Medicine invoice lines that are ready for pharmacy. Confirming a dispense is the only stock-out event."
      collapsed={collapsed}
      onToggleSidebar={onToggleSidebar}
      primaryAction={
        // PRD.290 — New Pharmacy Sale entry point (existing patients only).
        // Always available top-right; gated only by Billing permission +
        // Invoice module (same as the row Billing action).
        billingEnabled ? (
          <Button variant="gradient" size="md" onClick={() => setSaleDrawerOpen(true)}>
            + New Pharmacy Sale
          </Button>
        ) : undefined
      }
    >
      {/* PRD.291/204 — Today's Prescriptions / Ready / History tab switcher */}
      <PharmacyViewTabs
        viewMode={viewMode}
        onChange={(mode) => {
          setViewMode(mode);
          setSearch('');
        }}
        todaysCount={todaysRxRows.length}
        readyCount={queueRows.length}
        historyCount={historyRows.length}
      />

      {/* Per-tab description — sits between the tabs and the table so it
          describes the active tab's contents. */}
      <div
        style={{
          padding: 'var(--sp-3) var(--sp-6) 0',
          fontSize: 'var(--font-size-xs)',
          color: 'var(--text-muted)',
          flexShrink: 0,
        }}
      >
        {viewMode === 'today'
          ? "Today's prescriptions with un-invoiced medicines. Create an invoice to move them to Ready to Dispense."
          : viewMode === 'queue'
            ? 'Medicine invoice lines appear here once the invoice is cleared for dispense.'
            : 'Confirmed Pharmacy dispenses across all patients.'}
      </div>

      <InventoryListToolbar
        search={search}
        onSearchChange={setSearch}
        searchPlaceholder={
          viewMode === 'today'
            ? 'Search patient, doctor...'
            : viewMode === 'queue'
              ? 'Search patient, invoice, medicine, SKU...'
              : 'Search patient, medicine, SKU, dispense no, invoice, batch...'
        }
        filters={
          viewMode === 'history' ? (
            <select
              value={historyDateFilter}
              onChange={(e) =>
                setHistoryDateFilter(e.target.value as 'all' | 'today' | '7d' | '30d')
              }
              style={toolbarSelectStyle}
            >
              <option value="all">All dates</option>
              <option value="today">Today</option>
              <option value="7d">Last 7 days</option>
              <option value="30d">Last 30 days</option>
            </select>
          ) : viewMode === 'queue' ? (
            // PRD.289 §2 — queue sort control.
            <select
              value={sortKey}
              onChange={(e) => setSortKey(e.target.value as QueueSortKey)}
              aria-label="Sort queue"
              style={toolbarSelectStyle}
            >
              {SORT_OPTIONS.map((o) => (
                <option key={o.value} value={o.value}>
                  Sort: {o.label}
                </option>
              ))}
            </select>
          ) : undefined
        }
      />
      <div style={{ height: 'var(--sp-4)', flexShrink: 0 }} />

      {/* PRD.291 — Today's Prescriptions (billing queue). */}
      {viewMode === 'today' && (
        <TodaysPrescriptionsTable
          rows={filteredTodays}
          totalRowCount={todaysRxRows.length}
          canBill={billingEnabled}
          onView={(r) => {
            if (r.patient_id) {
              setRxDrawer({
                patientId: r.patient_id,
                encounterId: r.encounter_id,
                patientName: r.patient_name,
              });
            }
          }}
          onCreateInvoice={(r) => {
            if (r.patient_id) {
              setRxInvoiceDrawer({
                encounterId: r.encounter_id,
                patientId: r.patient_id,
                patientName: r.patient_name,
                date: r.prescription_date ? r.prescription_date.slice(0, 10) : todayIso,
              });
            }
          }}
          onCollectPayment={(r) => {
            // Reuse the PRD.289 Billing drawer focused on the pending draft.
            if (r.patient_id && r.pending_invoice_id) {
              setBillingDrawer({
                patientId: r.patient_id,
                encounterId: r.encounter_id,
                date: r.prescription_date ? r.prescription_date.slice(0, 10) : todayIso,
                invoiceId: r.pending_invoice_id,
              });
            }
          }}
        />
      )}

      {viewMode === 'queue' &&
        (filtered.length === 0 ? (
          // PRD.290 — empty state hosts the New Pharmacy Sale CTA.
          <PharmacyEmptyState
            searching={queueRows.length > 0}
            canSell={billingEnabled}
            onNewSale={() => setSaleDrawerOpen(true)}
          />
        ) : (
          <InventoryTable columns={COLUMNS}>
            {filtered.map((row) => {
              const state = lineState[row.invoice_item_id];
              const batchSelected = !!state?.batchId;
              const overAvailable =
                state?.batchAvailable != null &&
                (row.quantity_remaining ?? 0) > state.batchAvailable;
              return (
                <InventoryRow key={row.invoice_item_id} columns={COLUMNS}>
                  {/* Date */}
                  <div style={cellTopStyle}>
                    <div
                      style={{
                        ...primaryLineStyle,
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {row.invoice_date ?? '—'}
                    </div>
                  </div>

                  {/* Patient — opens read-only patient details drawer. */}
                  <div style={cellTopStyle}>
                    {row.patient_id ? (
                      <button
                        type="button"
                        onClick={() => setViewPatientId(row.patient_id)}
                        title="View patient details"
                        style={primaryLinkStyle}
                      >
                        {row.patient_name ?? '—'}
                      </button>
                    ) : (
                      <div
                        style={{
                          ...primaryLineStyle,
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text)',
                        }}
                      >
                        {row.patient_name ?? '—'}
                      </div>
                    )}
                  </div>

                  {/* PRD.289 — Prescription column: date + doctor open the
                    read-only Prescription drawer; hosts Print Rx. */}
                  <div style={cellTopStyle}>
                    {row.encounter_id && row.patient_id ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setRxDrawer({
                              patientId: row.patient_id!,
                              encounterId: row.encounter_id!,
                              patientName: row.patient_name,
                            })
                          }
                          title="View this visit's prescription"
                          style={{
                            background: 'none',
                            border: 'none',
                            padding: 0,
                            textAlign: 'left',
                            width: '100%',
                            minWidth: 0,
                            cursor: 'pointer',
                            fontFamily: 'inherit',
                            minHeight: PRIMARY_LINE_HEIGHT,
                            display: 'flex',
                            flexDirection: 'column',
                            justifyContent: 'center',
                            gap: 1,
                          }}
                        >
                          <span
                            style={{
                              fontSize: 'var(--font-size-sm)',
                              fontWeight: 'var(--font-weight-medium)',
                              color: 'var(--color-primary)',
                            }}
                          >
                            {formatShortDate(row.encounter_date) ?? 'View prescription'}
                          </span>
                          <span
                            style={{
                              fontSize: 'var(--font-size-xs)',
                              color: 'var(--text-muted)',
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap',
                            }}
                          >
                            {row.provider_name ? `Dr. ${row.provider_name}` : 'Prescribed by —'}
                          </span>
                        </button>
                        <div style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={() =>
                              setRxPrintRequest({
                                patientId: row.patient_id!,
                                encounterId: row.encounter_id!,
                              })
                            }
                            title="Print prescription using the encounter print layout"
                            style={secondaryActionStyle}
                          >
                            Print Rx
                          </button>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          ...primaryLineStyle,
                          fontSize: 'var(--font-size-sm)',
                          color: 'var(--text-muted)',
                        }}
                      >
                        —
                      </div>
                    )}
                  </div>

                  {/* PRD.289 — Invoice column: invoice number opens Billing
                    focused on this invoice; secondary "Open Billing" action. */}
                  <div style={cellTopStyle}>
                    {billingEnabled && row.patient_id && row.invoice_id ? (
                      <>
                        <button
                          type="button"
                          onClick={() =>
                            setBillingDrawer({
                              patientId: row.patient_id!,
                              encounterId: row.encounter_id,
                              date: row.invoice_date,
                              invoiceId: row.invoice_id,
                            })
                          }
                          title="Open Billing focused on this invoice"
                          style={primaryLinkStyle}
                        >
                          {row.invoice_no ?? row.invoice_id.slice(0, 8)}
                        </button>
                        <div style={{ marginTop: 4 }}>
                          <button
                            type="button"
                            onClick={() =>
                              setBillingDrawer({
                                patientId: row.patient_id!,
                                encounterId: row.encounter_id,
                                date: row.invoice_date,
                                invoiceId: row.invoice_id,
                              })
                            }
                            title="Open Billing (invoices & payments)"
                            style={secondaryActionStyle}
                          >
                            Open Billing
                          </button>
                        </div>
                      </>
                    ) : (
                      <div
                        style={{
                          ...primaryLineStyle,
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                        }}
                      >
                        {row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}
                      </div>
                    )}
                  </div>

                  {/* Medicine — opens the per-item Stock Card drawer. */}
                  <div style={cellTopStyle}>
                    {row.inventory_item_id ? (
                      <button
                        type="button"
                        onClick={() =>
                          setStockCardItem({
                            itemId: row.inventory_item_id!,
                            itemName: row.item_name ?? row.description_snapshot,
                            sku: row.sku,
                          })
                        }
                        title="View stock card (movement ledger) for this medicine"
                        style={{
                          ...primaryLinkStyle,
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.item_name ?? row.description_snapshot ?? '—'}
                      </button>
                    ) : (
                      <div
                        style={{
                          ...primaryLineStyle,
                          fontSize: 'var(--font-size-sm)',
                          fontWeight: 'var(--font-weight-medium)',
                          color: 'var(--text)',
                          overflow: 'hidden',
                          textOverflow: 'ellipsis',
                          whiteSpace: 'nowrap',
                        }}
                      >
                        {row.item_name ?? row.description_snapshot ?? '—'}
                      </div>
                    )}
                    {row.sku && (
                      <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                        SKU: {row.sku}
                      </div>
                    )}
                  </div>

                  {/* Batch picker */}
                  <div style={cellTopStyle}>
                    <button
                      type="button"
                      onClick={() => setBatchModalLineId(row.invoice_item_id)}
                      style={{
                        height: PRIMARY_LINE_HEIGHT,
                        padding: '0 8px',
                        borderRadius: 'var(--radius-sm)',
                        border: '1px solid var(--border)',
                        background: batchSelected ? 'var(--color-success-bg)' : 'var(--bg-surface)',
                        color: batchSelected ? 'var(--color-success-text)' : 'var(--text-muted)',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 500,
                        cursor: 'pointer',
                        width: '100%',
                        textAlign: 'left',
                      }}
                    >
                      {batchSelected
                        ? `${state.batchNumber}${
                            state?.batchAvailable != null ? ` · ${state.batchAvailable} avail` : ''
                          }`
                        : 'Select batch…'}
                    </button>
                  </div>

                  {/* Qty */}
                  <div style={cellTopStyle}>
                    <div
                      style={{
                        ...primaryLineStyle,
                        justifyContent: 'flex-end',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: overAvailable ? '#991b1b' : 'var(--text)',
                      }}
                    >
                      {row.quantity_remaining ?? 0} {row.inventory_unit ?? row.unit_snapshot ?? ''}
                    </div>
                  </div>

                  {/* Status */}
                  <div style={cellTopStyle}>
                    <div style={primaryLineStyle}>
                      <InventoryStatusBadge
                        status={(row.qty_dispensed ?? 0) > 0 ? 'partial' : 'draft'}
                        label={
                          (row.qty_dispensed ?? 0) > 0
                            ? `Partial (${row.qty_dispensed}/${row.qty_ordered ?? 0})`
                            : 'Ready'
                        }
                      />
                    </div>
                  </div>

                  {/* PRD.289 — Actions column owns only the pharmacy operation. */}
                  <div
                    style={{
                      ...cellTopStyle,
                      display: 'flex',
                      flexDirection: 'column',
                      alignItems: 'flex-end',
                      gap: 4,
                    }}
                  >
                    <div
                      style={{
                        minHeight: PRIMARY_LINE_HEIGHT,
                        display: 'flex',
                        alignItems: 'center',
                      }}
                    >
                      <Button
                        variant="gradient"
                        size="md"
                        onClick={() => confirmDispense(row)}
                        disabled={!batchSelected || state?.saving === true || overAvailable}
                      >
                        {state?.saving ? 'Saving...' : 'Confirm Dispense'}
                      </Button>
                    </div>
                    {state?.message && (
                      <span
                        style={{
                          fontSize: 10,
                          textAlign: 'right',
                          color:
                            state.message.kind === 'ok' ? 'var(--color-success-text)' : '#991b1b',
                        }}
                      >
                        {state.message.text}
                      </span>
                    )}
                  </div>
                </InventoryRow>
              );
            })}
          </InventoryTable>
        ))}

      {/* PRD.204 — Dispense History (read-only across all patients) */}
      {viewMode === 'history' && (
        <PharmacyHistoryTable
          rows={filteredHistory}
          totalRowCount={historyRows.length}
          onPrintLabel={(row) =>
            setLabelToPrint({
              patientName: row.patient_name ?? '',
              medicineName: row.item_name_snapshot ?? '',
              sku: row.sku_snapshot,
              instructions: row.instructions_snapshot,
              qty: row.quantity_dispensed,
              unit: row.unit_snapshot,
              batchNumber: row.batch_number_snapshot,
              expiryDate: row.expiry_date_snapshot,
              dispensedAt: row.dispensed_at,
              dispenseNumber: row.dispense_number,
              clinicName,
            })
          }
          onPrintRx={(row) => {
            if (row.patient_id && row.encounter_id) {
              setRxPrintRequest({
                patientId: row.patient_id,
                encounterId: row.encounter_id,
              });
            }
          }}
          onOpenPatient={(row) => {
            if (row.patient_id) navigate(`/patient/${row.patient_id}/pharmacy`);
          }}
        />
      )}

      {batchModalLineId && (
        <BatchPickerModal
          itemId={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.inventory_item_id ?? null
          }
          requiredQty={
            queueRows.find((r) => r.invoice_item_id === batchModalLineId)?.quantity_remaining ?? 0
          }
          portalStyle={brandTokens as CSSProperties}
          onClose={() => setBatchModalLineId(null)}
          onSelect={(batch) => {
            patchLine(batchModalLineId, {
              batchId: batch.inventory_batch_id,
              batchNumber: batch.batch_number,
              batchExpiry: batch.expiry_date,
              batchAvailable: batch.available_quantity,
              message: null,
            });
            setBatchModalLineId(null);
          }}
        />
      )}
      <PrescriptionPrintMount
        request={rxPrintRequest}
        onAfterPrint={() => setRxPrintRequest(null)}
      />
      <PharmacyLabelPrintMount data={labelToPrint} onAfterPrint={() => setLabelToPrint(null)} />

      {/* PRD.289 — read-only patient details from a queue row's patient name.
          Self-contained drawer; does not navigate to /patient/:id. */}
      <PatientDetailsDrawer
        open={viewPatientId != null}
        patientId={viewPatientId}
        onOpenChange={(o) => !o && setViewPatientId(null)}
      />

      {/* PRD.289 — per-medicine stock card (movement ledger) drawer. */}
      <PharmacyStockCardDrawer
        open={stockCardItem !== null}
        onOpenChange={(o) => !o && setStockCardItem(null)}
        context={stockCardItem}
      />

      {/* PRD.289 — read-only Prescription drawer. */}
      <PharmacyPrescriptionDrawer
        open={rxDrawer !== null}
        onOpenChange={(o) => !o && setRxDrawer(null)}
        context={rxDrawer}
      />

      {/* PRD.289 — Billing drawer. Reuses the existing Billing surface
          (invoices + payments) so no billing/payment logic is duplicated. */}
      {billingDrawer && (
        <TreatmentInvoiceDrawer
          open
          onOpenChange={(o) => !o && setBillingDrawer(null)}
          patientId={billingDrawer.patientId}
          initialEncounterId={billingDrawer.encounterId}
          initialDate={billingDrawer.date}
          initialInvoiceId={billingDrawer.invoiceId ?? undefined}
          drawerWidth={billingDrawerWidth}
        />
      )}

      {/* PRD.290 — New Pharmacy Sale (existing patients only). Reuses the
          existing patient search + the existing Billing surface; no duplicate
          billing/payment logic, no Patient Workspace navigation. */}
      <NewPharmacySaleDrawer
        open={saleDrawerOpen}
        onOpenChange={setSaleDrawerOpen}
        drawerWidth={billingDrawerWidth}
      />

      {/* PRD.291 — Today's Prescriptions → Create Invoice. Medicine selection
          then the existing Billing surface (auto-adds the selected lines). */}
      <TodaysPrescriptionInvoiceDrawer
        open={rxInvoiceDrawer !== null}
        onOpenChange={(o) => !o && setRxInvoiceDrawer(null)}
        context={rxInvoiceDrawer}
        drawerWidth={billingDrawerWidth}
      />
    </InventoryPageShell>
  );
}

// ─── PRD.290 — Ready-to-Dispense empty state + New Pharmacy Sale CTA ───

function PharmacyEmptyState({
  searching,
  canSell,
  onNewSale,
}: {
  /** True when the queue has rows but the current search hides them all. */
  searching: boolean;
  canSell: boolean;
  onNewSale: () => void;
}) {
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: 'var(--sp-4) var(--sp-6) var(--sp-6)' }}>
      <div
        style={{
          maxWidth: 720,
          margin: '0 auto',
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          padding: 'var(--sp-12) var(--sp-8)',
          display: 'flex',
          flexDirection: 'column',
          alignItems: 'center',
          textAlign: 'center',
          gap: 'var(--sp-3)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
          }}
        >
          {searching ? 'No matching medicines.' : 'No medicines are ready to dispense.'}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)', maxWidth: 420 }}>
          {searching
            ? 'Try a different search.'
            : 'Once an invoice is paid and ready for dispensing, it will appear here.'}
        </div>

        {!searching && canSell && (
          <>
            {/* ── or ── divider */}
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: 'var(--sp-3)',
                width: 'min(320px, 80%)',
                margin: 'var(--sp-2) 0',
                color: 'var(--text-muted)',
              }}
            >
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
              <span style={{ fontSize: 'var(--font-size-xs)' }}>or</span>
              <div style={{ flex: 1, height: 1, background: 'var(--border)' }} />
            </div>
            <Button variant="gradient" size="lg" onClick={onNewSale}>
              + New Pharmacy Sale
            </Button>
          </>
        )}
      </div>
    </div>
  );
}

// ─── PRD.291 — Today's Prescriptions billing-status chevron strip ───
//
// Two interlocking chevrons (Create Invoice → Collect Payment) that read like
// the Queue status strip: green = done, primary = the obvious next click,
// muted = still ahead. A prescription is only ever in this queue while unpaid,
// so "Collect Payment" never shows as completed here.

const RX_CHEVRON_DEPTH = 10;
const RX_CHEVRON_HEIGHT = 30;
const RX_CHEVRON_CLIP = {
  first: `polygon(0 0, calc(100% - ${RX_CHEVRON_DEPTH}px) 0, 100% 50%, calc(100% - ${RX_CHEVRON_DEPTH}px) 100%, 0 100%)`,
  last: `polygon(0 0, 100% 0, 100% 100%, 0 100%, ${RX_CHEVRON_DEPTH}px 50%)`,
} as const;

type RxStepVariant = 'completed' | 'next' | 'future';

function RxStepChevron({
  label,
  variant,
  position,
  onClick,
}: {
  label: string;
  variant: RxStepVariant;
  position: 'first' | 'last';
  onClick?: () => void;
}) {
  const background =
    variant === 'completed'
      ? '#22c55e'
      : variant === 'next'
        ? 'var(--color-primary)'
        : 'var(--bg-surface-2)';
  const color = variant === 'future' ? 'var(--text-muted)' : '#fff';
  const clickable = variant === 'next' && !!onClick;
  return (
    <button
      type="button"
      disabled={!clickable}
      onClick={clickable ? onClick : undefined}
      title={
        variant === 'completed'
          ? `${label} — done`
          : variant === 'future'
            ? `${label} — not yet`
            : label
      }
      style={{
        height: RX_CHEVRON_HEIGHT,
        padding: position === 'first' ? '0 16px' : '0 14px 0 20px',
        marginLeft: position === 'first' ? 0 : -RX_CHEVRON_DEPTH,
        background,
        color,
        border: 'none',
        borderRadius: 0,
        clipPath: RX_CHEVRON_CLIP[position],
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        fontFamily: 'inherit',
        whiteSpace: 'nowrap',
        display: 'inline-flex',
        alignItems: 'center',
        gap: 4,
        cursor: clickable ? 'pointer' : 'default',
        flexShrink: 0,
      }}
    >
      {variant === 'completed' && <span aria-hidden="true">✓</span>}
      {label}
    </button>
  );
}

// ─── PRD.291 — Today's Prescriptions table ───

function TodaysPrescriptionsTable({
  rows,
  totalRowCount,
  canBill,
  onView,
  onCreateInvoice,
  onCollectPayment,
}: {
  rows: TodaysPrescriptionRow[];
  totalRowCount: number;
  canBill: boolean;
  onView: (row: TodaysPrescriptionRow) => void;
  onCreateInvoice: (row: TodaysPrescriptionRow) => void;
  onCollectPayment: (row: TodaysPrescriptionRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <div style={{ padding: 'var(--sp-8)', textAlign: 'center', color: 'var(--text-muted)' }}>
        <div
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 4,
          }}
        >
          {totalRowCount === 0
            ? 'No prescriptions to invoice today.'
            : 'No prescriptions match your search.'}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)' }}>
          {totalRowCount === 0
            ? "Today's prescriptions with un-invoiced medicines appear here."
            : 'Try a different search.'}
        </div>
      </div>
    );
  }
  return (
    <div style={{ flex: 1, overflowY: 'auto', padding: '0 var(--sp-6) var(--sp-6)' }}>
      <div
        style={{
          background: 'var(--bg-surface)',
          borderRadius: 'var(--radius-lg)',
          border: '1px solid var(--border)',
          overflow: 'hidden',
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr>
              <Th>Date / Time</Th>
              <Th>Patient</Th>
              <Th>Prescribing Doctor</Th>
              <Th>Prescription</Th>
              <Th align="right">Actions</Th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const toBill = (r.billable_meds ?? 0) - (r.invoiced_meds ?? 0);
              const awaitingPayment = (r.invoiced_meds ?? 0) - (r.paid_meds ?? 0);
              // While any billable line is un-invoiced → bill it. Otherwise all
              // are on an invoice but some remain unpaid → collect payment.
              const mode: 'bill' | 'pay' = toBill > 0 ? 'bill' : 'pay';
              return (
                <tr key={r.encounter_id} style={{ borderTop: '1px solid var(--border)' }}>
                  <Td>
                    <span style={{ color: 'var(--text-muted)' }}>
                      {r.prescription_date
                        ? r.prescription_date.replace('T', ' ').slice(0, 16)
                        : '—'}
                    </span>
                  </Td>
                  <Td>
                    <span style={{ fontWeight: 'var(--font-weight-medium)', color: 'var(--text)' }}>
                      {r.patient_name ?? '—'}
                    </span>
                  </Td>
                  <Td>{r.provider_name ? `Dr. ${r.provider_name}` : '—'}</Td>
                  <Td>
                    <div style={{ color: 'var(--text)' }}>
                      {r.total_meds} medicine{r.total_meds === 1 ? '' : 's'}
                    </div>
                    <div style={{ fontSize: 'var(--font-size-xs)', color: 'var(--text-muted)' }}>
                      {mode === 'bill'
                        ? `${toBill} of ${r.billable_meds} to bill`
                        : `Awaiting payment · ${awaitingPayment} of ${r.billable_meds}`}
                    </div>
                  </Td>
                  <Td align="right">
                    <div style={{ display: 'inline-flex', alignItems: 'center', gap: 10 }}>
                      <button
                        type="button"
                        onClick={() => onView(r)}
                        title="View prescription (read-only)"
                        style={{
                          height: RX_CHEVRON_HEIGHT,
                          padding: '0 10px',
                          borderRadius: 'var(--radius-sm)',
                          border: '1px solid var(--border)',
                          background: 'var(--bg-surface)',
                          color: 'var(--text)',
                          fontSize: 'var(--font-size-xs)',
                          fontWeight: 500,
                          cursor: 'pointer',
                        }}
                      >
                        View Prescription
                      </button>
                      {canBill && (
                        // Create Invoice → Collect Payment status chevrons.
                        <div style={{ display: 'inline-flex' }}>
                          <RxStepChevron
                            label="Create Invoice"
                            position="first"
                            variant={mode === 'bill' ? 'next' : 'completed'}
                            onClick={() => onCreateInvoice(r)}
                          />
                          <RxStepChevron
                            label="Collect Payment"
                            position="last"
                            variant={mode === 'pay' ? 'next' : 'future'}
                            onClick={r.pending_invoice_id ? () => onCollectPayment(r) : undefined}
                          />
                        </div>
                      )}
                    </div>
                  </Td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

// ─── PRD.204 — view-mode tabs ───

function PharmacyViewTabs({
  viewMode,
  onChange,
  todaysCount,
  readyCount,
  historyCount,
}: {
  viewMode: 'today' | 'queue' | 'history';
  onChange: (mode: 'today' | 'queue' | 'history') => void;
  todaysCount: number;
  readyCount: number;
  historyCount: number;
}) {
  // PRD.291 — Today's Prescriptions sits before Ready to Dispense.
  const TABS: { value: 'today' | 'queue' | 'history'; label: string; count: number }[] = [
    { value: 'today', label: "Today's Prescriptions", count: todaysCount },
    { value: 'queue', label: 'Ready to Dispense', count: readyCount },
    { value: 'history', label: 'Dispense History', count: historyCount },
  ];
  return (
    <div
      style={{
        display: 'flex',
        gap: 'var(--sp-3)',
        padding: 'var(--sp-3) var(--sp-6) 0',
        borderBottom: '1px solid var(--border)',
        flexShrink: 0,
      }}
    >
      {TABS.map((t) => {
        const active = viewMode === t.value;
        return (
          <button
            key={t.value}
            type="button"
            onClick={() => onChange(t.value)}
            style={{
              padding: 'var(--sp-2) var(--sp-3)',
              border: 'none',
              borderBottom: active ? '2px solid var(--color-primary)' : '2px solid transparent',
              background: 'transparent',
              color: active ? 'var(--text)' : 'var(--text-muted)',
              fontSize: 'var(--font-size-sm)',
              fontWeight: active ? 'var(--font-weight-semi)' : 'var(--font-weight-medium)',
              cursor: 'pointer',
              display: 'flex',
              alignItems: 'center',
              gap: 6,
              marginBottom: -1,
            }}
          >
            {t.label}
            <span
              style={{
                display: 'inline-flex',
                alignItems: 'center',
                justifyContent: 'center',
                minWidth: 22,
                height: 18,
                padding: '0 6px',
                borderRadius: 'var(--radius-full)',
                background: active ? 'var(--color-primary)' : 'var(--bg-surface-2)',
                color: active ? '#fff' : 'var(--text-muted)',
                fontSize: 10,
                fontWeight: 700,
              }}
            >
              {t.count}
            </span>
          </button>
        );
      })}
    </div>
  );
}

// ─── PRD.204 — Dispense History table ───

function PharmacyHistoryTable({
  rows,
  totalRowCount,
  onPrintLabel,
  onPrintRx,
  onOpenPatient,
}: {
  rows: PatientPharmacyDispenseHistoryRow[];
  totalRowCount: number;
  onPrintLabel: (row: PatientPharmacyDispenseHistoryRow) => void;
  onPrintRx: (row: PatientPharmacyDispenseHistoryRow) => void;
  onOpenPatient: (row: PatientPharmacyDispenseHistoryRow) => void;
}) {
  if (rows.length === 0) {
    return (
      <div
        style={{
          padding: 'var(--sp-8)',
          textAlign: 'center',
          color: 'var(--text-muted)',
        }}
      >
        <div
          style={{
            fontSize: 'var(--font-size-md)',
            fontWeight: 'var(--font-weight-semi)',
            color: 'var(--text)',
            marginBottom: 4,
          }}
        >
          {totalRowCount === 0
            ? 'No dispenses recorded yet.'
            : 'No dispense history matches your filters.'}
        </div>
        <div style={{ fontSize: 'var(--font-size-sm)' }}>
          {totalRowCount === 0
            ? 'Confirmed Pharmacy dispenses appear here automatically.'
            : 'Try adjusting your search or date filter.'}
        </div>
      </div>
    );
  }
  return (
    <div
      style={{
        margin: '0 var(--sp-6) var(--sp-6)',
        border: '1px solid var(--border)',
        borderRadius: 'var(--radius-lg)',
        overflow: 'hidden',
      }}
    >
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr>
            <Th>Dispense No.</Th>
            <Th>Date</Th>
            <Th>Patient</Th>
            <Th>Medicine</Th>
            <Th align="right">Qty</Th>
            <Th>Batch</Th>
            <Th>Expiry</Th>
            <Th>Invoice</Th>
            <Th align="right">Actions</Th>
          </tr>
        </thead>
        <tbody>
          {rows.map((row) => (
            <tr
              key={row.pharmacy_dispense_line_id}
              style={{ borderTop: '1px solid var(--border)' }}
            >
              <Td>
                <span
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    color: 'var(--text)',
                  }}
                >
                  {row.dispense_number ?? row.pharmacy_dispense_id?.slice(0, 8) ?? '—'}
                </span>
              </Td>
              <Td>
                <span style={{ fontSize: 'var(--font-size-sm)', color: 'var(--text-muted)' }}>
                  {row.dispensed_at ? row.dispensed_at.replace('T', ' ').slice(0, 16) : '—'}
                </span>
              </Td>
              <Td>
                <button
                  type="button"
                  onClick={() => onOpenPatient(row)}
                  disabled={!row.patient_id}
                  style={{
                    background: 'none',
                    border: 'none',
                    padding: 0,
                    color: row.patient_id ? 'var(--color-primary)' : 'var(--text)',
                    cursor: row.patient_id ? 'pointer' : 'default',
                    fontSize: 'var(--font-size-sm)',
                    fontWeight: 'var(--font-weight-medium)',
                    textAlign: 'left',
                  }}
                  title={row.patient_id ? 'Open patient pharmacy' : undefined}
                >
                  {row.patient_name ?? '—'}
                </button>
              </Td>
              <Td>
                <div
                  style={{
                    fontSize: 'var(--font-size-sm)',
                    color: 'var(--text)',
                  }}
                >
                  {row.item_name_snapshot ?? '—'}
                </div>
                {row.sku_snapshot && (
                  <div
                    style={{
                      fontSize: 'var(--font-size-xs)',
                      color: 'var(--text-muted)',
                    }}
                  >
                    SKU: {row.sku_snapshot}
                  </div>
                )}
              </Td>
              <Td align="right">
                {row.quantity_dispensed ?? 0} {row.unit_snapshot ?? ''}
              </Td>
              <Td>{row.batch_number_snapshot ?? '—'}</Td>
              <Td>{row.expiry_date_snapshot ?? '—'}</Td>
              <Td>{row.invoice_no ?? row.invoice_id?.slice(0, 8) ?? '—'}</Td>
              <Td align="right">
                <div style={{ display: 'inline-flex', gap: 6 }}>
                  <button
                    type="button"
                    onClick={() => onPrintRx(row)}
                    disabled={!row.encounter_id || !row.patient_id}
                    title={
                      row.encounter_id && row.patient_id
                        ? 'Print prescription using the encounter print layout'
                        : "Prescription print is available from the encounter's Prescriptions tab."
                    }
                    style={{
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color:
                        row.encounter_id && row.patient_id ? 'var(--text)' : 'var(--text-disabled)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 500,
                      cursor: row.encounter_id && row.patient_id ? 'pointer' : 'not-allowed',
                    }}
                  >
                    Print Rx
                  </button>
                  <button
                    type="button"
                    onClick={() => onPrintLabel(row)}
                    title="Print medicine label using dispense snapshots"
                    style={{
                      height: 28,
                      padding: '0 8px',
                      borderRadius: 'var(--radius-sm)',
                      border: '1px solid var(--border)',
                      background: 'var(--bg-surface)',
                      color: 'var(--text)',
                      fontSize: 'var(--font-size-xs)',
                      fontWeight: 500,
                      cursor: 'pointer',
                    }}
                  >
                    Print Label
                  </button>
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

function Td({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <td
      style={{
        padding: '10px 12px',
        textAlign: align,
        fontSize: 'var(--font-size-sm)',
        color: 'var(--text)',
        verticalAlign: 'top',
      }}
    >
      {children}
    </td>
  );
}

// ─── Batch picker modal — existing batches only (PRD.194 §12) ───

function BatchPickerModal({
  itemId,
  requiredQty,
  portalStyle,
  onClose,
  onSelect,
}: {
  itemId: string | null;
  requiredQty: number;
  portalStyle: CSSProperties;
  onClose: () => void;
  onSelect: (batch: PharmacyBatchOptionRow) => void;
}) {
  const { data: batchRows = [] } = usePowerSyncQuery<PharmacyBatchOptionRow>(
    itemId
      ? qAvailableBatchesForDispense(itemId)
      : // Always-empty placeholder query when no item is selected
        {
          key: ['pharmacy-available-batches', '__none__'] as const,
          sql: `SELECT NULL AS inventory_batch_id, NULL AS batch_number, NULL AS expiry_date, 0 AS available_quantity WHERE 1=0`,
          params: [],
          watch: ['inventory_batches', 'stock_movements'],
        },
  );

  return (
    <Modal
      open
      onOpenChange={(v) => !v && onClose()}
      title="Select Batch"
      maxWidth={560}
      portalStyle={portalStyle}
      footer={
        <div style={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Button variant="outline" size="md" onClick={onClose}>
            Close
          </Button>
        </div>
      }
    >
      <div style={{ padding: 'var(--sp-3) var(--sp-5) var(--sp-5)' }}>
        <p
          style={{
            margin: '0 0 var(--sp-3)',
            fontSize: 'var(--font-size-xs)',
            color: 'var(--text-muted)',
          }}
        >
          Pick an existing batch with at least <strong>{requiredQty}</strong> available. New batches
          are created via Delivery Order receiving or Inventory Adjustments — not from Pharmacy.
        </p>
        {batchRows.length === 0 ? (
          <div
            style={{
              padding: 'var(--sp-6)',
              textAlign: 'center',
              fontSize: 'var(--font-size-sm)',
              color: 'var(--text-muted)',
            }}
          >
            No available batches for this medicine. Receive stock or create an inventory adjustment
            first.
          </div>
        ) : (
          <table style={{ width: '100%', borderCollapse: 'collapse' }}>
            <thead>
              <tr>
                <Th>Batch Number</Th>
                <Th>Expiry</Th>
                <Th align="right">Available</Th>
                <Th />
              </tr>
            </thead>
            <tbody>
              {batchRows.map((b) => {
                const avail = b.available_quantity ?? 0;
                const insufficient = avail < requiredQty;
                return (
                  <tr
                    key={b.inventory_batch_id}
                    style={{ borderBottom: '1px solid var(--border)' }}
                  >
                    <td style={{ padding: '8px 6px', fontSize: 'var(--font-size-sm)' }}>
                      {b.batch_number ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        fontSize: 'var(--font-size-sm)',
                        color: 'var(--text-muted)',
                      }}
                    >
                      {b.expiry_date ?? '—'}
                    </td>
                    <td
                      style={{
                        padding: '8px 6px',
                        textAlign: 'right',
                        fontSize: 'var(--font-size-sm)',
                        fontWeight: 'var(--font-weight-medium)',
                        color: insufficient ? '#991b1b' : 'var(--text)',
                      }}
                    >
                      {avail}
                    </td>
                    <td style={{ padding: '8px 6px', textAlign: 'right' }}>
                      <Button
                        variant="outline"
                        size="md"
                        disabled={avail <= 0}
                        onClick={() => onSelect(b)}
                      >
                        Select
                      </Button>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
    </Modal>
  );
}

function Th({
  children,
  align = 'left',
}: {
  children?: React.ReactNode;
  align?: 'left' | 'right' | 'center';
}) {
  return (
    <th
      style={{
        padding: '10px 8px',
        textAlign: align,
        fontSize: 'var(--font-size-xs)',
        fontWeight: 'var(--font-weight-semi)',
        color: 'var(--text-muted)',
        textTransform: 'uppercase',
        letterSpacing: '0.04em',
        borderBottom: '1px solid var(--border)',
        background: 'var(--bg-surface-2, #fafafa)',
      }}
    >
      {children}
    </th>
  );
}
